import { useEffect, useRef, useState } from "react";
import { motion, animate, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { HeartHandshake, ArrowUpRight, Loader2, HandCoins, Users } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay, testid }) {
  return (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, ease: EASE, delay }}
        data-testid={testid}
      >
        {children}
      </motion.span>
    </span>
  );
}

function CountUp({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState("$0");

  useEffect(() => {
    if (!inView || !value) return;
    const controls = animate(0, value, {
      duration: 2.4,
      ease: EASE,
      onUpdate: (v) =>
        setDisplay(
          v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
        ),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span ref={ref} data-testid="tips-total-amount">
      {display}
    </span>
  );
}

const money = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const fmtDate = (d) =>
  new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" });

export default function Charity() {
  const [summary, setSummary] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    Promise.all([axios.get(`${API}/charity/summary`), axios.get(`${API}/charity/updates`)])
      .then(([s, u]) => {
        setSummary(s.data);
        setUpdates(u.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="charity-page">
      <section className="relative pt-44 pb-24 md:pb-32 overflow-hidden" data-testid="charity-hero">
        <div className="absolute top-20 -left-40 w-[500px] h-[500px] bg-brand-yellow/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 -right-40 w-[500px] h-[500px] bg-brand-green/10 rounded-full blur-[140px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
            className="inline-flex items-center gap-2 rounded-full border border-brand-green/40 bg-brand-green/10 px-4 py-1.5 mb-8"
            data-testid="charity-badge"
          >
            <HeartHandshake className="w-3.5 h-3.5 text-brand-green" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-brand-green">
              The EzRueda Tips Fund
            </span>
          </motion.div>

          <h1 className="font-display font-semibold uppercase leading-[0.95] tracking-tight text-[12vw] sm:text-[9vw] lg:text-[6.5rem]">
            <MaskedLine delay={0.35} testid="charity-headline-line-1">
              Where your
            </MaskedLine>
            <MaskedLine delay={0.5} testid="charity-headline-line-2">
              <span className="text-brand-yellow">tips</span> <span className="text-brand-green">go.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
            className="mt-8 max-w-xl text-zinc-300 text-base md:text-lg leading-relaxed"
            data-testid="charity-subcopy"
          >
            Every tip a rider adds in the EzRueda app is pooled into one fund and given back to
            Albany — community fridges, driver hardship grants, free rides to medical care.
            100% donated. Zero kept.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.05 }}
            className="mt-16 border-t border-white/10 pt-12"
            data-testid="tips-total-block"
          >
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-zinc-500 mb-6">
              Total tips donated so far
            </p>
            {loading ? (
              <Loader2 className="w-10 h-10 text-brand-yellow animate-spin" data-testid="tips-total-loading" />
            ) : (
              <>
                <p className="font-display font-bold tracking-tight leading-none text-6xl sm:text-7xl lg:text-8xl text-white">
                  <CountUp value={summary?.total_donated || 0} />
                </p>
                <p className="mt-5 text-sm text-zinc-500 max-w-lg leading-relaxed" data-testid="tips-total-caption">
                  Live from the EzRueda tips ledger — the exact same data the rider app reads,
                  so this page and the app always match.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 mt-12 max-w-xl">
                  <div data-testid="charity-stat-tips">
                    <p className="font-display font-semibold text-3xl md:text-4xl text-brand-yellow">
                      {(summary?.tips_count || 0).toLocaleString("en-US")}
                    </p>
                    <p className="text-xs text-zinc-500 mt-2 uppercase tracking-[0.15em]">Tips given by riders</p>
                  </div>
                  <div data-testid="charity-stat-drivers">
                    <p className="font-display font-semibold text-3xl md:text-4xl text-brand-green">
                      {summary?.drivers_supported || 0}
                    </p>
                    <p className="text-xs text-zinc-500 mt-2 uppercase tracking-[0.15em]">Drivers supported</p>
                  </div>
                  <div data-testid="charity-stat-share">
                    <p className="font-display font-semibold text-3xl md:text-4xl text-white">100%</p>
                    <p className="text-xs text-zinc-500 mt-2 uppercase tracking-[0.15em]">Of tips donated</p>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </section>

      <section className="relative py-24 md:py-32 border-t border-white/10" data-testid="charity-mission-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: EASE }}
              className="lg:col-span-5"
            >
              <p className="text-xs font-bold tracking-[0.3em] uppercase text-zinc-500 mb-5">Our mission</p>
              <h2 className="font-display font-semibold tracking-tight leading-[1.02] text-4xl sm:text-5xl">
                100% of tips. <span className="text-brand-yellow">0% to us.</span>
              </h2>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
              className="lg:col-span-7 space-y-6 text-zinc-400 leading-relaxed text-base md:text-lg"
            >
              <p>
                <span className="font-display font-semibold text-5xl text-brand-green float-left mr-3 leading-[0.85] mt-1">T</span>
                ips on EzRueda were never meant to pad our margins. So we built the Tips Fund:
                every dollar a rider adds as a tip is pooled, and every dollar of that pool is
                donated back into the city that rides with us.
              </p>
              <p>
                The fund moves money three ways — stocking Albany's community fridges, covering
                emergency hardship grants so drivers never have to choose between a repair bill
                and rent, and sponsoring free rides to medical appointments for neighbors who
                can't afford the trip.
              </p>
              <p>
                No administrative skims, no fine print. EzRueda covers the fund's operating costs
                separately, which means the number you see above is the number Albany actually
                receives.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="relative py-24 md:py-32 border-t border-white/10" data-testid="charity-updates-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE }}
            className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
          >
            <div>
              <p className="text-xs font-bold tracking-[0.3em] uppercase text-zinc-500 mb-5">
                Updates &amp; stories
              </p>
              <h2 className="font-display font-semibold tracking-tight leading-[1.02] text-4xl sm:text-5xl max-w-2xl">
                The money, <span className="text-brand-green">in motion.</span>
              </h2>
            </div>
            <p className="text-zinc-500 text-sm max-w-sm leading-relaxed">
              Every month we publish where the fund went — receipts, not slogans.
            </p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-16" data-testid="updates-loading">
              <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {updates.map((u, i) => (
                <motion.article
                  key={u.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.8, ease: EASE, delay: i * 0.12 }}
                  className="group rounded-3xl border border-white/10 bg-brand-surface p-8 flex flex-col hover:border-brand-yellow/40 transition-colors duration-500"
                  data-testid={`charity-update-card-${i}`}
                >
                  <div className="flex items-center justify-between mb-8">
                    <span className="rounded-full bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow text-[10px] font-bold tracking-[0.2em] uppercase px-3.5 py-1.5">
                      {u.tag}
                    </span>
                    <span className="text-xs text-zinc-500 font-medium">{fmtDate(u.date)}</span>
                  </div>
                  {u.amount != null && (
                    <p className="font-display font-bold text-4xl text-white tracking-tight mb-4">
                      {money(u.amount)}
                    </p>
                  )}
                  <h3 className="font-display font-semibold text-xl md:text-2xl tracking-tight mb-4 leading-snug">
                    {u.title}
                  </h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{u.body}</p>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden" data-testid="charity-cta-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-brand-yellow/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE }}
            className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10"
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <HandCoins className="w-6 h-6 text-brand-yellow" />
                <Users className="w-6 h-6 text-brand-green" />
              </div>
              <h2 className="font-display font-semibold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl max-w-2xl">
                Ride with <span className="text-brand-yellow">purpose.</span>
              </h2>
              <p className="mt-6 text-zinc-400 max-w-md text-base md:text-lg leading-relaxed">
                Your next trip across Albany can stock a fridge, fix a driver's car, or get a
                neighbor to their appointment. Tip in the app — we handle the rest.
              </p>
            </div>
            <Link
              to="/#rider-signup"
              data-testid="charity-cta-button"
              className="group inline-flex items-center gap-3 rounded-full bg-brand-yellow text-black font-bold px-8 py-4 shrink-0 hover:bg-brand-yellow-hover hover:scale-105 hover:shadow-[0_0_40px_rgba(255,204,0,0.35)] transition-all duration-300"
            >
              Get your first ride
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
